<script lang="ts">
	// The design system's Chip (design/components/core/Chip.jsx): a short label
	// that says what state a thing is in. Not a control: nothing about it is
	// tappable. Tones are named for what they mean, never for their colour.
	//
	// Dashed means not yet, solid means wrong: unconfirmed and flag share the
	// amber and differ only in the border.
	//
	// `dot` is a status read in a table column, not a label on a thing: a
	// filled chip in a row of text buttons reads as one more button. The dot
	// takes the tone through the accent tokens, which lift under dark, and the
	// words stay a text token, so both read on any ground. Quiet and
	// unconfirmed are a hollow ring: off, or not yet, rather than wrong.

	interface Props {
		text: string;
		tone?: 'ok' | 'flag' | 'flag-solid' | 'neutral' | 'strong' | 'quiet' | 'unconfirmed' | 'live';
		size?: 'sm' | 'md' | 'lg';
		dot?: boolean;
		testId?: string;
	}

	let { text, tone = 'neutral', size = 'sm', dot = false, testId }: Props = $props();
</script>

{#if dot}
	<span class="dotted {tone} {size}" data-testid={testId}>
		<span class="mark" aria-hidden="true"></span>{text}
	</span>
{:else}
	<span class="chip {tone} {size}" data-testid={testId}>{text}</span>
{/if}

<style>
	.chip {
		display: inline-block;
		flex: none;
		font-family: var(--font-sans);
		font-weight: 700;
		white-space: nowrap;
	}

	.chip.sm {
		font-size: 12px;
		padding: var(--chip-pad);
		border-radius: var(--radius-chip);
	}

	.chip.md {
		font-size: 13px;
		padding: var(--chip-pad-lg);
		border-radius: var(--radius-chip);
	}

	.chip.lg {
		font-size: 17px;
		padding: 8px 18px;
		border-radius: var(--radius-tag);
	}

	.chip.ok {
		background: var(--ledger);
		color: var(--on-ledger);
	}

	.chip.flag {
		background: var(--surface-flag);
		color: var(--ink-flag-deep);
		border: 1px solid var(--border-flag);
	}

	.chip.flag-solid {
		background: var(--surface-flag-solid);
		color: var(--on-flag);
	}

	.chip.neutral {
		background: var(--paper);
		color: var(--ink-body);
		border: 1px solid var(--rule);
	}

	/* The emphasis surface, so it inverts with the ground. */
	.chip.strong {
		background: var(--surface-invert);
		color: var(--on-ink);
	}

	.chip.quiet {
		background: var(--paper);
		color: var(--ink-muted);
		border: 1px dashed var(--rule);
	}

	.chip.unconfirmed {
		background: var(--surface-flag);
		color: var(--ink-flag-deep);
		border: 1px dashed var(--ink-flag);
	}

	.chip.live {
		background: var(--speed);
		color: var(--on-speed);
	}

	/* The dot form. Its own class, so none of the filled tones above reach it:
	   same tone names, different pairs. */
	.dotted {
		display: inline-flex;
		align-items: center;
		gap: var(--space-3);
		flex: none;
		font-family: var(--font-sans);
		font-weight: 600;
		white-space: nowrap;
		color: var(--text-body);
	}

	.dotted.sm {
		font-size: 12px;
	}

	.dotted.md {
		font-size: 13px;
	}

	.dotted.lg {
		font-size: 17px;
	}

	.mark {
		inline-size: 8px;
		block-size: 8px;
		flex: none;
		border-radius: var(--radius-pill);
		background: var(--text-muted);
	}

	.dotted.ok,
	.dotted.live,
	.dotted.strong {
		color: var(--text-strong);
	}

	.dotted.ok .mark {
		background: var(--accent-visibility);
	}

	.dotted.live .mark {
		background: var(--accent-speed);
	}

	.dotted.strong .mark {
		background: var(--text-strong);
	}

	.dotted.flag,
	.dotted.flag-solid,
	.dotted.unconfirmed {
		color: var(--ink-flag-deep);
	}

	.dotted.flag .mark {
		background: var(--accent-notice);
	}

	.dotted.flag-solid .mark {
		background: var(--surface-flag-solid);
	}

	.dotted.quiet {
		color: var(--text-muted);
	}

	.dotted.quiet .mark {
		background: transparent;
		box-shadow: inset 0 0 0 1.5px var(--text-muted);
	}

	.dotted.unconfirmed .mark {
		background: transparent;
		box-shadow: inset 0 0 0 1.5px var(--ink-flag);
	}
</style>
